/**
 * Patient home — five departments, not one list of doctors.
 *
 * This screen used to answer one question: which doctor do I want. With
 * hospitals, labs and centres on the platform it has to answer four more —
 * where do I get this scan, what does that surgery cost, is there an ICU bed,
 * where is the nearest pharmacy — and a single scrolling page cannot hold that
 * without burying the thing you came for.
 *
 * So: a horizontal tab strip, each department in its own colour, remembered
 * between visits. The consultations tab is first and is what opens by default,
 * because it is still the common case.
 *
 * Replaces `initPatientDashboard` / `renderPatientCategoryCards` /
 * `renderPatientCategories`.
 */

import { useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { CategoryGrid } from './CategoryGrid';
import { ServiceCatalog } from './services/ServiceCatalog';
import { Icon, Panel, PageHeader, StatCard, Spinner, EmptyState } from '../../components/common';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { usePatientBookings } from '../../hooks/useBookings';
import { usePatientPrescriptions } from '../../hooks/usePrescriptions';
import { describeBooking, statusLabel, statusStyle } from '../../domain/bookings';
import { PATIENT_TABS } from '../../domain/institutions';
import { resolveClinicRgb } from '../doctor/calendar/legacyTheme';

export function PatientHome() {
  const { session, patientId } = useAuth();
  const { categories, categoriesState } = useData();
  const { buckets, loading: bookingsLoading } = usePatientBookings(patientId);
  const { prescriptions } = usePatientPrescriptions(patientId);

  // Remembered, because a patient who came for lab results should not have to
  // walk past the doctor list every time.
  const [activeId, setActiveId] = useLocalStorage('capsule_patient_tab', PATIENT_TABS[0].id);

  const active = useMemo(
    () => PATIENT_TABS.find((tab) => tab.id === activeId) || PATIENT_TABS[0],
    [activeId],
  );

  const stripRef = useRef(null);

  // Keep the chosen tab on screen. With five tabs on a phone the last two sit
  // off the edge, and restoring a remembered tab would otherwise leave the
  // strip scrolled to the start with nothing apparently selected.
  useEffect(() => {
    const strip = stripRef.current;
    if (!strip) return;
    const current = strip.querySelector('[aria-selected="true"]');
    current?.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
  }, [activeId]);

  const nextBooking = buckets.upcoming[0] || buckets.pending[0] || null;

  return (
    <div className="space-y-6">
      <PageHeader
        title={`أهلاً، ${session?.name || 'بك'} 👋`}
        subtitle="كل رعايتك الصحية من مكان واحد — أطباء، تحاليل، عمليات ورعاية"
        icon="heart-pulse"
      />

      {/* Snapshot */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard icon="calendar-check" label="حجوزات قادمة" value={buckets.upcoming.length} accent="cyan" />
        <StatCard icon="clock" label="بانتظار التأكيد" value={buckets.pending.length} accent="amber" />
        <StatCard icon="file-text" label="الروشتات" value={prescriptions.length} accent="emerald" />
        <StatCard icon="history" label="زيارات سابقة" value={buckets.past.length} accent="purple" />
      </div>

      {/* Next appointment */}
      <Panel>
        <h3 className="mb-4 flex items-center gap-2 text-lg font-bold text-white">
          <Icon name="calendar-clock" size={20} className="text-cyan-400" />
          موعدك القادم
        </h3>

        {bookingsLoading ? (
          <Spinner variant="inline" />
        ) : nextBooking ? (
          <Link
            to="/patient/appointments"
            className="flex flex-wrap items-center gap-3 rounded-xl border border-white/[0.07] bg-white/[0.04] p-4 transition-colors hover:border-cyan-500/40"
          >
            <span className="rounded-xl bg-cyan-500/10 p-3 text-cyan-300">
              <Icon name="stethoscope" size={20} />
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate font-bold text-white">{nextBooking.doctorName}</p>
              <p className="truncate text-xs text-slate-400">{describeBooking(nextBooking)}</p>
            </div>
            <span
              className={`rounded-full border px-2.5 py-1 text-[11px] font-bold ${statusStyle(nextBooking.status)}`}
            >
              {statusLabel(nextBooking.status)}
            </span>
          </Link>
        ) : (
          <EmptyState
            icon="calendar-plus"
            title="لا توجد مواعيد قادمة"
            message="اختر ما تحتاجه من الأقسام بالأسفل لتحجز."
          />
        )}
      </Panel>

      {/* The departments */}
      <div>
        <div className="svc-tabs" role="tablist" ref={stripRef} aria-label="أقسام الخدمات">
          {PATIENT_TABS.map((tab) => {
            const selected = tab.id === active.id;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={selected}
                onClick={() => setActiveId(tab.id)}
                className="svc-tab"
                style={{ '--tab-accent': resolveClinicRgb(tab.accent).join(', ') }}
              >
                <span className="svc-tab__icon">
                  <Icon name={tab.icon} size={15} />
                </span>
                {tab.label}
                {tab.kind === 'soon' && (
                  <span className="rounded-full bg-white/10 px-1.5 py-0.5 text-[9px] font-bold text-slate-300">
                    قريباً
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className="mt-5" role="tabpanel" aria-label={active.label}>
          {active.kind === 'consultations' && (
            <ConsultationsTab categories={categories} loading={categoriesState.loading} />
          )}

          {active.kind === 'services' && <ServiceCatalog tab={active} />}

          {active.kind === 'soon' && <ComingSoon tab={active} />}
        </div>
      </div>
    </div>
  );
}

/* ========================================================================== */

function ConsultationsTab({ categories, loading }) {
  if (loading && categories.length === 0) {
    return <Spinner label="جاري تحميل التخصصات..." />;
  }

  return (
    <div className="space-y-4">
      <p className="px-1 text-xs text-slate-400">اختر التخصص المناسب لحالتك</p>
      <CategoryGrid categories={categories} />
    </div>
  );
}

/**
 * The pharmacy tab.
 *
 * Declared in PATIENT_TABS with no categories behind it rather than added
 * later, so the navigation is complete now and nothing shifts sideways when
 * pharmacies arrive.
 */
function ComingSoon({ tab }) {
  return (
    <Panel
      padded={false}
      className="svc-card rounded-2xl"
      style={{ '--tab-accent': resolveClinicRgb(tab.accent).join(', ') }}
    >
      <div className="svc-card__bar" aria-hidden="true" />
      <div className="flex flex-col items-center gap-4 p-12 text-center">
        <span className="svc-card__icon grid h-16 w-16 place-items-center rounded-2xl">
          <Icon name={tab.icon} size={30} />
        </span>
        <div>
          <h3 className="text-lg font-bold text-white">{tab.label} — قريباً</h3>
          <p className="mt-1.5 max-w-md text-sm leading-relaxed text-slate-400">
            نعمل على ربط الصيدليات لتطلب أدويتك وتقارن أسعارها وتستلمها في بيتك، من نفس المكان الذي
            تحجز منه كشوفاتك.
          </p>
        </div>
      </div>
    </Panel>
  );
}

export default PatientHome;
